import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { from, Observable, of, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ISnMapResponse,
  ISnListResponse,
  ISnMapQueryParams,
  ISnListQueryParameters,
  ISnFeedQueryParameters,
} from '@lib/api';
import { FsTipDisposition, SnTip } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnTipsAPI {
  private _targetedTip$ = new Subject<SnTip>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public setDispositionTitle(tip: SnTip): void {
    this._targetedTip$.next(tip);
  }

  public getDispositionTitle(): Observable<SnTip> {
    return this._targetedTip$.asObservable();
  }

  public getTipList(params: ISnListQueryParameters): Observable<ISnListResponse<SnTip>> {
    return this._httpClient.get('/api/tips/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((tip) => new SnTip(tip)),
      })),
    );
  }

  public feedTips(params: ISnFeedQueryParameters): Observable<SnTip[]> {
    return this._httpClient
      .get('/api/tips/feed/', { params })
      .pipe(map((response: any) => response.map((tip) => new SnTip(tip))));
  }

  public getTip(id: string): Observable<SnTip> {
    return this._httpClient.get(`/api/tips/${id}`).pipe(map((response) => new SnTip(response)));
  }

  public closeTip(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'map.tips.closeConfirmDialog',
        confirmButton: {
          text: 'common.close',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.post(`/api/tips/${id}/close/`, {})),
        tap({
          next: () => this._toastrService.success(t('vault.tips.messages.tipClosed')),
          error: () =>
            this._toastrService.error(t('vault.tips.errors.cannotCloseTip')),
        }),
      );
  }

  public deleteTip(tip: SnTip): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'map.tips.deleteConfirmDialog',
        confirmButton: {
          text: 'common.delete',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/tips/${tip.id}/`)),
        tap({
          next: () => this._toastrService.success(t('vault.tips.messages.tipDeleted')),
          error: () =>
            this._toastrService.error(t('vault.tips.errors.cannotDeleteTip')),
        }),
      );
  }

  public updateTip(id: string, data: any): Observable<SnTip> {
    return this._httpClient.patch(`/api/tips/${id}/`, data).pipe(
      map((response: any) => new SnTip(response)),
      tap({
        next: () => this._toastrService.success(t('vault.tips.messages.tipUpdated')),
        error: () => this._toastrService.error(t('vault.tips.errors.couldNotUpdateTip')),
      }),
    );
  }

  public downloadMedia(id: string): Observable<any> {
    return this._httpClient
      .get(`/api/tips/${id}/download/`, {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        switchMap((res: HttpEvent<any>) => {
          if (res.type === HttpEventType.Response) {
            if (res.body.type === 'application/zip') {
              return of(res);
            }
            return from(res.body?.text()).pipe(
              switchMap((response: any) => {
                const resObj = JSON.parse(response);
                if (resObj.async) {
                  return of(resObj);
                }
                return this._httpClient.get(resObj.location, {
                  responseType: 'blob',
                  reportProgress: true,
                  observe: 'events',
                });
              }),
            );
          }
          return of(res);
        }),
      );
  }

  public downloadMeta(id: string): Observable<Blob> {
    return this._httpClient.get(`/api/tips/${id}/meta/`, { responseType: 'blob' }).pipe(
      tap((response: Blob) => {
        saveAs(response, `tip${id}-meta`);
      }),
    );
  }

  public getTipMap(
    params: ISnListQueryParameters | ISnMapQueryParams,
  ): Observable<ISnMapResponse<SnTip>> {
    return this._httpClient.get('/api/maps/static/tips/', { params }).pipe(
      map((response: any) => {
        return {
          results: response.results.map((tip) => new SnTip(tip)),
          clusterResults: response.clusterResults || [],
        };
      }),
    );
  }

  public getTipDisposions(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsTipDisposition>> {
    return this._httpClient.get('/api/tips/dispositions/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        links: response,
        results: response.results.map((tip) => new FsTipDisposition(tip)),
      })),
    );
  }

  public createTipDisposition(data: any): Observable<any> {
    return this._httpClient.post('/api/tips/dispositions/', data).pipe(
      tap({
        next: () => this._toastrService.success(t('vault.tips.messages.dispositionCreated')),
        error: () =>
          this._toastrService.error(
            t('vault.tips.errors.unableToCreateDisposition'),
            t('vault.tips.errors.dispositionMustBeUnique'),
          ),
      }),
    );
  }

  public getDisposition(id: string): Observable<FsTipDisposition> {
    return this._httpClient
      .get(`/api/tips/dispositions/${id}/`)
      .pipe(map((response) => new FsTipDisposition(response)));
  }

  public updateDisposition(id: string, data: any): Observable<FsTipDisposition> {
    return this._httpClient.patch(`/api/tips/dispositions/${id}/`, data).pipe(
      map((response: any) => new FsTipDisposition(response)),
      tap({
        next: () => this._toastrService.success(t('vault.tips.messages.dispositionUpdated')),
        error: () =>
          this._toastrService.error(t('vault.tips.errors.couldNotUpdateDisposition')),
      }),
    );
  }

  public deleteDisposition(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'map.tips.disposition.deleteConfirmDialog',
        confirmButton: {
          text: 'common.delete',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/tips/dispositions/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('vault.tips.messages.dispositionDeleted')),
          error: () =>
            this._toastrService.error(t('vault.tips.errors.cannotDeleteDisposition')),
        }),
      );
  }
}
